import { codelistStore } from '@shared/codelist/codelist-store';

export type IsoPointStyle =
    | 'dot'
    | 'triangle'
    | 'rectangle'
    | 'diamond'
    | 'star'
    | 'circle'
    | 'filled_triangle'
    | 'filled_rectangle'
    | 'filled_diamond'
    | 'filled_star'
    | 'filled_circle'
    | 'cross'
    | 'plus'
    | 'tree'
    | 'pin'
    | 'rectangle_dot'
    | 'rectangle_plus'
    | 'rectangle_cross'
    | 'rectangle_pin'
    | 'rectangle_circle'
    | 'rectangle_circle_dot'
    | 'rectangle_circle_plus'
    | 'diamond_cross'
    | 'circle_dot'
    | 'circle_plus'
    | 'circle_cross'
    | 'circle_pin'
    | 'circle_circle'
    | 'circle_filled_circle';

export const ISO_POINT_STYLES: IsoPointStyle[] = [
    'dot',
    'triangle',
    'rectangle',
    'diamond',
    'star',
    'circle',
    'filled_triangle',
    'filled_rectangle',
    'filled_diamond',
    'filled_star',
    'filled_circle',
    'cross',
    'plus',
    'tree',
    'pin',
    'rectangle_dot',
    'rectangle_plus',
    'rectangle_cross',
    'rectangle_pin',
    'rectangle_circle',
    'rectangle_circle_dot',
    'rectangle_circle_plus',
    'diamond_cross',
    'circle_dot',
    'circle_plus',
    'circle_cross',
    'circle_pin',
    'circle_circle',
    'circle_filled_circle',
];

interface SpriteSheet {
    src: string;
    width: number;
    height: number;
    size: number;
    offsetMap: any[];
    defaultOffset: number[];
}

let cachedSprite: SpriteSheet | null = null;
const symbolCanvasCache: Map<string, HTMLCanvasElement> = new Map();

export function getIsoPointStyleFromCode(codeName?: string): IsoPointStyle | null {
    if (!codeName) {
        return null;
    }

    const code = codelistStore.getCodeByName(codeName);
    if (!code || !code.pointstyle) {
        return null;
    }

    return ISO_POINT_STYLES.includes(code.pointstyle as IsoPointStyle) ? (code.pointstyle as IsoPointStyle) : null;
}

export function getGtPointWebGlStyle() {
    const useIsoCodelists = codelistStore.codelist !== null; // toggle here if the ISO symbol rendering needs to be disabled quickly
    return useIsoCodelists ? getIsoPointWebGlStyle() : getDefaultCircleWebGlStyle();
}

function getIsoPointWebGlStyle() {
    const sprite = getIsoSpriteSheet();
    return {
        filter: ['!=', ['get', 'hidden', 'string'], 'true'],
        'icon-src': sprite.src,
        'icon-height': sprite.height,
        'icon-width': sprite.width,
        'icon-size': [sprite.size, sprite.size],
        'icon-offset': ['match', ['get', 'pointstyle'], ...sprite.offsetMap, sprite.defaultOffset],
        'icon-color': ['match', ['get', 'hover'], 1, '#0ca7ff', ['color', ['get', 'colorR'], ['get', 'colorG'], ['get', 'colorB'], 0.8]],
        'icon-scale': ['interpolate', ['exponential', 2], ['zoom'], 8, 0.16, 15, 0.42],
        'icon-opacity': 1,
    };
}

function getDefaultCircleWebGlStyle() {
    return {
        filter: ['!=', ['get', 'hidden', 'string'], 'true'],
        'circle-stroke-width': 2,
        'circle-stroke-color': 'rgba(255,255,255,0.05)',
        'circle-radius': 3.5,
        'circle-fill-color': ['match', ['get', 'hover'], 1, '#0ca7ff', ['color', ['get', 'colorR'], ['get', 'colorG'], ['get', 'colorB'], 0.8]],
        'circle-opacity': 1,
        'circle-scale': ['interpolate', ['exponential', 2], ['zoom'], 8, 0.2, 15, 1],
    };
}

function getIsoSpriteSheet(): SpriteSheet {
    if (cachedSprite) return cachedSprite;

    const size = 32;
    const defaultIndex = ISO_POINT_STYLES.indexOf('filled_circle');
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size * ISO_POINT_STYLES.length;
    const ctx = canvas.getContext('2d');

    const offsetMap: any[] = [];

    ISO_POINT_STYLES.forEach((style, index) => {
        ctx.save();
        ctx.translate(size / 2, size / 2 + index * size);
        ctx.lineWidth = Math.max(2, size * 0.08);
        ctx.strokeStyle = '#fff';
        ctx.fillStyle = '#fff';
        drawIsoSymbol(ctx, style, size * 0.8);
        ctx.restore();
        offsetMap.push(style);
        offsetMap.push([0, index * size]);
    });

    cachedSprite = {
        src: canvas.toDataURL(),
        width: canvas.width,
        height: canvas.height,
        size,
        offsetMap,
        defaultOffset: [0, defaultIndex >= 0 ? defaultIndex * size : 0],
    };
    return cachedSprite;
}

export function drawIsoSymbol(ctx: CanvasRenderingContext2D, style: IsoPointStyle, radius: number) {
    const r = radius / 2;
    const drawPolygon = (sides: number, rad: number, rotate = -Math.PI / 2, fill = false) => {
        ctx.beginPath();
        for (let i = 0; i < sides; i++) {
            const angle = rotate + (i * 2 * Math.PI) / sides;
            const x = Math.cos(angle) * rad;
            const y = Math.sin(angle) * rad;
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath();
        fill ? ctx.fill() : ctx.stroke();
    };

    const drawStar = (points: number, outer: number, inner: number, fill = false) => {
        ctx.beginPath();
        const step = Math.PI / points;
        for (let i = 0; i < points * 2; i++) {
            const rad = i % 2 === 0 ? outer : inner;
            const angle = -Math.PI / 2 + i * step;
            const x = Math.cos(angle) * rad;
            const y = Math.sin(angle) * rad;
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath();
        fill ? ctx.fill() : ctx.stroke();
    };

    const outlineCircle = () => {
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.stroke();
    };

    const fillCircle = (rad = r * 0.6) => {
        ctx.beginPath();
        ctx.arc(0, 0, rad, 0, Math.PI * 2);
        ctx.fill();
    };

    switch (style) {
        case 'dot':
            (() => {
                const dotR = r * 0.75;
                fillCircle(dotR);
            })();
            break;
        case 'triangle':
            drawPolygon(3, r);
            break;
        case 'rectangle':
            ctx.strokeRect(-r, -r, r * 2, r * 2);
            break;
        case 'diamond':
            drawPolygon(4, r, -Math.PI / 2);
            break;
        case 'star':
            drawStar(5, r * 1.2, r * 0.6);
            break;
        case 'circle':
            outlineCircle();
            break;
        case 'filled_triangle':
            drawPolygon(3, r, -Math.PI / 2, true);
            break;
        case 'filled_rectangle':
            ctx.fillRect(-r, -r, r * 2, r * 2);
            break;
        case 'filled_diamond':
            drawPolygon(4, r, -Math.PI / 2, true);
            break;
        case 'filled_star':
            drawStar(5, r * 1.2, r * 0.6, true);
            break;
        case 'filled_circle':
            fillCircle(r);
            break;
        case 'cross':
            ctx.beginPath();
            ctx.moveTo(-r, -r);
            ctx.lineTo(r, r);
            ctx.moveTo(r, -r);
            ctx.lineTo(-r, r);
            ctx.stroke();
            break;
        case 'plus':
            ctx.beginPath();
            ctx.moveTo(-r, 0);
            ctx.lineTo(r, 0);
            ctx.moveTo(0, -r);
            ctx.lineTo(0, r);
            ctx.stroke();
            break;
        case 'tree':
            (() => {
                const treeR = r * 1.6; // slightly larger tree symbol
                drawPolygon(3, treeR * 0.9, -Math.PI / 2, true);
                ctx.fillRect(-treeR * 0.15, treeR * 0.2, treeR * 0.3, treeR * 0.6);
            })();
            break;
        case 'pin':
            (() => {
                const headR = r * 1.05; // larger head
                const headCY = -r * 0.25; // lift the head so the point sits lower
                const tailLen = r * 2.6; // long tail to a clear point
                const tailWidth = headR * 0.6;

                // Outer shape: head + tail as one filled path
                const tailTopY = headCY + headR * 0.6;
                const tailTipY = tailTopY + tailLen;
                ctx.beginPath();
                // head arc
                ctx.arc(0, headCY, headR, Math.PI, 0, false);
                // right side down to tail base
                ctx.quadraticCurveTo(headR, tailTopY, tailWidth * 0.6, tailTopY + headR * 0.2);
                // tail point
                ctx.lineTo(0, tailTipY);
                // left side back up
                ctx.lineTo(-tailWidth * 0.6, tailTopY + headR * 0.2);
                ctx.quadraticCurveTo(-headR, tailTopY, -headR, headCY);
                ctx.closePath();
                ctx.fill('evenodd');

                // Inner hole
                ctx.save();
                ctx.globalCompositeOperation = 'destination-out';
                ctx.beginPath();
                ctx.arc(0, headCY, headR * 0.45, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            })();
            break;
        case 'rectangle_dot':
            ctx.strokeRect(-r, -r * 0.75, r * 2, r * 1.5);
            fillCircle(r * 0.25);
            break;
        case 'rectangle_plus':
            (() => {
                const cellHalf = radius / 1.6;
                const rectHalfW = Math.min(r * 1.4, cellHalf * 0.95);
                const rectHalfH = Math.min(r * 1.05, cellHalf * 0.85);
                ctx.strokeRect(-rectHalfW, -rectHalfH, rectHalfW * 2, rectHalfH * 2);
                ctx.beginPath();
                const plusW = rectHalfW * 0.75;
                const plusH = rectHalfH * 0.75;
                ctx.moveTo(-plusW, 0);
                ctx.lineTo(plusW, 0);
                ctx.moveTo(0, -plusH);
                ctx.lineTo(0, plusH);
                ctx.stroke();
            })();
            break;
        case 'rectangle_cross':
            (() => {
                const cellHalf = radius / 1.6;
                const rectHalfW = Math.min(r * 1.4, cellHalf * 0.95);
                const rectHalfH = Math.min(r * 1.05, cellHalf * 0.85);
                ctx.strokeRect(-rectHalfW, -rectHalfH, rectHalfW * 2, rectHalfH * 2);
                ctx.beginPath();
                const crossW = rectHalfW * 0.75;
                const crossH = rectHalfH * 0.75;
                ctx.moveTo(-crossW, -crossH);
                ctx.lineTo(crossW, crossH);
                ctx.moveTo(crossW, -crossH);
                ctx.lineTo(-crossW, crossH);
                ctx.stroke();
            })();
            break;
        case 'rectangle_pin':
            (() => {
                // Larger rectangle encompassing the pin
                const cellHalf = radius / 1.6;
                const rectHalfW = Math.min(r * 1.45, cellHalf * 0.98);
                const rectHalfH = Math.min(r * 1.15, cellHalf * 0.9);
                ctx.strokeRect(-rectHalfW, -rectHalfH, rectHalfW * 2, rectHalfH * 2);
                ctx.save();
                ctx.translate(0, -r * 0.05);
                drawIsoSymbol(ctx, 'pin', r * 1.1);
                ctx.restore();
            })();
            break;
        case 'rectangle_circle':
            (() => {
                // Larger rectangle that clearly envelopes a smaller circle
                const cellHalf = radius / 1.6; // sprite cell half-size
                const rectHalfW = Math.min(r * 1.45, cellHalf * 0.98);
                const rectHalfH = Math.min(r * 1.15, cellHalf * 0.9);
                ctx.strokeRect(-rectHalfW, -rectHalfH, rectHalfW * 2, rectHalfH * 2);

                const circleR = Math.min(rectHalfW, rectHalfH) * 0.6; // leave side edges visible
                ctx.beginPath();
                ctx.arc(0, 0, circleR, 0, Math.PI * 2);
                ctx.stroke();
            })();
            break;
        case 'rectangle_circle_dot':
            (() => {
                // Larger rectangle with enveloped circle and centered dot
                const cellHalf = radius / 1.6;
                const rectHalfW = Math.min(r * 1.45, cellHalf * 0.98);
                const rectHalfH = Math.min(r * 1.15, cellHalf * 0.9);
                ctx.strokeRect(-rectHalfW, -rectHalfH, rectHalfW * 2, rectHalfH * 2);

                const circleR = Math.min(rectHalfW, rectHalfH) * 0.6;
                ctx.beginPath();
                ctx.arc(0, 0, circleR, 0, Math.PI * 2);
                ctx.stroke();

                fillCircle(circleR * 0.4);
            })();
            break;
        case 'rectangle_circle_plus':
            (() => {
                const scale = 1.8; // target larger size
                const cellHalf = radius / 1.6; // sprite cell half-size
                const maxHalf = cellHalf * 0.99; // small margin to avoid clipping
                const rectHalfW = Math.min(r * scale, maxHalf);
                const rectHalfH = Math.min(r * scale * 0.9, maxHalf * 0.9);
                ctx.strokeRect(-rectHalfW, -rectHalfH, rectHalfW * 2, rectHalfH * 2);

                const circleR = Math.min(rectHalfW, rectHalfH) * 0.65; // larger circle
                ctx.beginPath();
                ctx.arc(0, 0, circleR, 0, Math.PI * 2);
                ctx.stroke();

                ctx.beginPath();
                const plusR = circleR * 0.7; // larger plus for legibility
                ctx.moveTo(-plusR, 0);
                ctx.lineTo(plusR, 0);
                ctx.moveTo(0, -plusR);
                ctx.lineTo(0, plusR);
                ctx.stroke();
            })();
            break;
        case 'diamond_cross':
            (() => {
                const cellHalf = radius / 1.6;
                const diamondR = Math.min(r * 1.3, cellHalf * 0.95);
                drawPolygon(4, diamondR, -Math.PI / 2);
                ctx.beginPath();
                const crossR = diamondR * 0.75;
                ctx.moveTo(0, -crossR);
                ctx.lineTo(0, crossR);
                ctx.moveTo(-crossR, 0);
                ctx.lineTo(crossR, 0);
                ctx.stroke();
            })();
            break;
        case 'circle_dot':
            outlineCircle();
            fillCircle(r * 0.35);
            break;
        case 'circle_plus':
            outlineCircle();
            ctx.beginPath();
            ctx.moveTo(-r * 0.55, 0);
            ctx.lineTo(r * 0.55, 0);
            ctx.moveTo(0, -r * 0.55);
            ctx.lineTo(0, r * 0.55);
            ctx.stroke();
            break;
        case 'circle_cross':
            outlineCircle();
            ctx.beginPath();
            ctx.moveTo(-r * 0.55, -r * 0.55);
            ctx.lineTo(r * 0.55, r * 0.55);
            ctx.moveTo(r * 0.55, -r * 0.55);
            ctx.lineTo(-r * 0.55, r * 0.55);
            ctx.stroke();
            break;
        case 'circle_pin':
            (() => {
                const cellHalf = radius / 1.6;
                const circleR = Math.min(r * 1.2, cellHalf * 0.95); // larger circle with margin
                ctx.beginPath();
                ctx.arc(0, 0, circleR, 0, Math.PI * 2);
                ctx.stroke();
                ctx.save();
                ctx.translate(0, -r * 0.08);
                drawIsoSymbol(ctx, 'pin', r * 1.05); // slightly larger pin inside
                ctx.restore();
            })();
            break;
        case 'circle_circle':
            outlineCircle();
            ctx.beginPath();
            ctx.arc(0, 0, r * 0.55, 0, Math.PI * 2);
            ctx.stroke();
            break;
        case 'circle_filled_circle':
            outlineCircle();
            fillCircle(r * 0.55);
            break;
        default:
            outlineCircle();
    }
}

export function createIsoSymbolCanvas(style: IsoPointStyle, color: string, size = 64): HTMLCanvasElement {
    const cacheKey = `${style}-${color}-${size}`;
    const cached = symbolCanvasCache.get(cacheKey);
    if (cached) return cached;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    ctx.save();
    ctx.translate(size / 2, size / 2);
    ctx.lineWidth = Math.max(2, size * 0.08);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    drawIsoSymbol(ctx, style, size * 0.8);
    ctx.restore();

    symbolCanvasCache.set(cacheKey, canvas);
    return canvas;
}
